import { clsx } from '@alexis/helpers/clsx';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Image from '@components/Image';
import Price from '@components/Price';

import {
  cloudflareImageTransformations,
  cloudinaryImageTransformations,
  isCloudinarySrc,
} from '@helpers/imageTransformations';
import { convertHotelRatePriceToPrice } from '@helpers/price';
import { getReviewScoreDisplay } from '@helpers/review';

import StarIcon from '@icons/star_rounded.svg';
import TickIcon from '@icons/tick.svg';

import { useReviewRatingType } from '@hooks/useReviewRatingType';

import {
  getCurrencyState,
  getExchangeRateState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { SimilarHotelViewModel } from '@wegoTypes/hotels/similarHotelViewModel';

import styles from './HotelMiniCard.module.scss';

interface HotelMiniCardProps {
  href: string;
  similarHotelViewModel: SimilarHotelViewModel;
  className?: string;
  onCardClick?: () => void;
}

const HotelMiniCard: React.FC<HotelMiniCardProps> = ({
  href,
  similarHotelViewModel,
  onCardClick,
  className,
}): JSX.Element => {
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const currency = useSelector(getCurrencyState);
  const translations = useSelector(getTranslationsState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const locale: string = useSelector(getLocaleState);

  const { getReviewRatingTypeColorClassName, getReviewRatingTypeDisplay } = useReviewRatingType();

  const noImagePlaceholderSrc = cloudinaryImageTransformations(
    'https://assets.wego.com/image/upload/v1639463186/web/misc/hotel-image-placeholder.png',
    'h_184',
    'c_fill',
    'f_auto',
    'fl_lossy',
    'q_auto:low',
    'g_auto',
  );

  const imageSrc = useMemo<string>(() => {
    if (similarHotelViewModel.hotel.images.length > 0) {
      if (isCloudinarySrc(similarHotelViewModel.hotel.images[0].url)) {
        return cloudinaryImageTransformations(
          similarHotelViewModel.hotel.images[0].url,
          'h_368',
          'c_fill',
          'f_auto',
          'fl_lossy',
          'q_auto:best',
          'g_auto',
        );
      } else {
        return cloudflareImageTransformations(similarHotelViewModel.hotel.images[0].url, {
          format: 'auto',
          height: 368,
          quality: 90,
        });
      }
    }
    return '';
  }, [similarHotelViewModel]);

  const paymentTypeRateAmenities = useMemo(() => {
    if (!similarHotelViewModel.rateAmenities) {
      return;
    }
    return similarHotelViewModel.rateAmenities
      .filter(
        (rateAmenity) =>
          rateAmenity.type === 'PAYMENT' && rateAmenity.code !== 'NO_CREDIT_CARD_NEEDED',
      )
      .slice(0, 2);
  }, [similarHotelViewModel]);

  return (
    <a
      className={clsx(
        styles.container,
        !similarHotelViewModel.hotel.bestRate && styles.soldOut,
        className,
      )}
      href={href}
      target='_blank'
      rel='noreferrer'
      onClick={onCardClick}
    >
      <Image
        className={styles.image}
        isLazy={true}
        noImagePlaceholderSrc={noImagePlaceholderSrc}
        placeholderSrc={noImagePlaceholderSrc}
        src={imageSrc}
      />

      {!similarHotelViewModel.hotel.bestRate ? (
        <div className={styles.tag}>{translations.soldout}</div>
      ) : null}

      <div className={styles.content}>
        {/* Hotel Name */}
        <div className={styles.name}>{similarHotelViewModel.hotel.name}</div>

        {!!similarHotelViewModel.hotel.star || !!similarHotelViewModel.hotel.districtName ? (
          <div className={styles.starAndDistrict}>
            {/* Star */}
            {!!similarHotelViewModel.hotel.star ? (
              <>
                {[...Array(similarHotelViewModel.hotel.star).keys()].map((value: number) => {
                  return <StarIcon key={value} className={styles.star} />;
                })}
              </>
            ) : null}

            {/* District */}
            {!!similarHotelViewModel.hotel.districtName ? (
              <div className={styles.district}>
                {!!similarHotelViewModel.hotel.star ? <span>・</span> : null}
                {similarHotelViewModel.hotel.districtName}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Review */}
        {!!similarHotelViewModel.hotel.reviews[0] ? (
          <div className={styles.review}>
            <div
              className={clsx(
                styles.score,
                getReviewRatingTypeColorClassName(
                  similarHotelViewModel.hotel.reviews[0].score,
                  styles,
                ),
              )}
            >
              {translateNumber(
                getReviewScoreDisplay(similarHotelViewModel.hotel.reviews[0].score),
                locale === 'fa',
              )}
            </div>

            <div className={styles.reviewTypeAndCount}>
              {getReviewRatingTypeDisplay(
                similarHotelViewModel.hotel.reviews[0]?.score,
                translations as { [key: string]: string },
              )}

              <div className={styles.reviewCount}>
                ・
                {translateText(
                  translations.count_review,
                  locale,
                  similarHotelViewModel.hotel.reviews[0]?.count,
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.noReview}>{translations.no_reviews}</div>
        )}

        {/* Rate Amenities */}
        {paymentTypeRateAmenities && paymentTypeRateAmenities.length > 0 ? (
          <div className={styles.rateAmenities}>
            {paymentTypeRateAmenities.map((paymentTypeRateAmenity) => (
              <div key={paymentTypeRateAmenity.code} className={styles.rateAmenity}>
                <TickIcon className={styles.icon} />
                <div className={styles.label}>{paymentTypeRateAmenity.name}</div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Rate */}
        {!!similarHotelViewModel.hotel.bestRate ? (
          <div className={styles.rate}>
            <Price
              className={styles.price}
              price={convertHotelRatePriceToPrice(
                similarHotelViewModel.hotel.bestRate.price,
                priceOptions.showTotalPrice,
                priceOptions.isIncludingTaxOrFee,
              )}
              locale={locale}
              currency={currency}
              exchangeRate={exchangeRate}
              isInline
            />
            <div className={styles.priceType}>
              {priceOptions.showTotalPrice ? translations.total_stay : translations.hotel_per_night}
            </div>
          </div>
        ) : null}
      </div>
    </a>
  );
};

export default HotelMiniCard;
